import React from 'react';

import { importRouteWithRetry, isChunkLoadError, recoverFromChunkError } from '../utils/chunkErrors';

// Single source of truth for route-level code splitting.
//
// `pageLoaders` is exported next to the lazy components on purpose: the prefetch
// helper (routes/prefetch.js) must call the EXACT same `import()` expressions so
// that webpack resolves both to the same chunk. Prefetching a different specifier
// would download a second copy of the page.
export const pageLoaders = {
  dashboard: () => import('../pages/Dashboard'),
  customers: () => import('../pages/Customers'),
  packagesAndParents: () => import('../pages/PackagesAndParents'),
  billing: () => import('../pages/Billing'),
  subscriptionDetails: () => import('../pages/SubscriptionDetails'),
  payments: () => import('../pages/Payments'),
  creditLedger: () => import('../pages/CreditLedger'),
  shop: () => import('../pages/Shop'),
  inventory: () => import('../pages/Inventory'),
  support: () => import('../pages/Support'),
  network: () => import('../pages/Network'),
  vlans: () => import('../pages/network/VlanManagement'),
  hotspot: () => import('../pages/network/Hotspot'),
  routerDetails: () => import('../pages/network/RouterDetails'),
  monitoring: () => import('../pages/network/Monitoring'),
  customerTraffic: () => import('../pages/network/CustomerTraffic'),
  reports: () => import('../pages/Reports'),
  settings: () => import('../pages/Settings'),
  renewalLanding: () => import('../pages/RenewalLanding'),
  printDocument: () => import('../pages/print/PrintDocument'),
  printIspDocument: () => import('../pages/print/PrintIspDocument')
};

/**
 * `React.lazy` + retry.
 *
 * A dropped packet over the links this app runs on used to replace the whole
 * screen with "Loading chunk N failed" while the chunk sat intact on the server.
 * `importRouteWithRetry` re-requests it; if it is still unavailable the cached
 * copy is suspect (a truncated body, or a shell from before the last deploy), so
 * the caches are dropped and the page reloads once. A second failure in the same
 * minute surfaces through ErrorBoundary with an explanation instead of looping.
 */
export const lazyWithRetry = (loader) => React.lazy(() => importRouteWithRetry(loader).catch((error) => {
  if (!isChunkLoadError(error)) throw error;
  recoverFromChunkError();
  throw error;
}));

export const Dashboard = lazyWithRetry(pageLoaders.dashboard);
export const Customers = lazyWithRetry(pageLoaders.customers);
export const PackagesAndParents = lazyWithRetry(pageLoaders.packagesAndParents);
export const Billing = lazyWithRetry(pageLoaders.billing);
export const SubscriptionDetails = lazyWithRetry(pageLoaders.subscriptionDetails);
export const Payments = lazyWithRetry(pageLoaders.payments);
export const CreditLedger = lazyWithRetry(pageLoaders.creditLedger);
export const Shop = lazyWithRetry(pageLoaders.shop);
export const Inventory = lazyWithRetry(pageLoaders.inventory);
export const Support = lazyWithRetry(pageLoaders.support);
export const Network = lazyWithRetry(pageLoaders.network);
export const VlanManagement = lazyWithRetry(pageLoaders.vlans);
export const Hotspot = lazyWithRetry(pageLoaders.hotspot);
export const RouterDetails = lazyWithRetry(pageLoaders.routerDetails);
export const MonitoringDashboard = lazyWithRetry(pageLoaders.monitoring);
export const CustomerTraffic = lazyWithRetry(pageLoaders.customerTraffic);
export const Reports = lazyWithRetry(pageLoaders.reports);
export const Settings = lazyWithRetry(pageLoaders.settings);
export const RenewalLanding = lazyWithRetry(pageLoaders.renewalLanding);
export const PrintDocument = lazyWithRetry(pageLoaders.printDocument);
export const PrintIspDocument = lazyWithRetry(pageLoaders.printIspDocument);

